'use client';

// useFeedback - Hook for managing attendee feedback on bot responses
// Story 4.4: Attendee Feedback on Answer Quality
//
// Features:
// - Fetch existing feedback on mount (GET /api/bots/[botId]/feedback)
// - Submit/update feedback (POST /api/bots/[botId]/feedback) with optimistic UI
// - localStorage cache for instant state restoration before API responds
// - Error handling with optimistic revert

import { useState, useEffect, useCallback, useRef } from 'react';
import type { FeedbackRating, FeedbackEntry } from './types';

export type { FeedbackRating, FeedbackEntry };

export interface UseFeedbackOptions {
  botId: string;
  sessionId: string;
  enabled?: boolean;
}

export interface UseFeedbackReturn {
  feedbackMap: Record<string, FeedbackEntry>;
  submitFeedback: (
    messageId: string,
    rating: FeedbackRating,
    comment?: string
  ) => void;
  isSubmitting: boolean;
  error: Error | null;
}

function getStorageKey(botId: string, sessionId: string): string {
  return `boty_consumer_feedback_${botId}_${sessionId}`;
}

function loadFromStorage(botId: string, sessionId: string): Record<string, FeedbackEntry> {
  try {
    const key = getStorageKey(botId, sessionId);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as Record<string, FeedbackEntry>;
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

function saveToStorage(
  botId: string,
  sessionId: string,
  map: Record<string, FeedbackEntry>
): void {
  try {
    const key = getStorageKey(botId, sessionId);
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

export function useFeedback({
  botId,
  sessionId,
  enabled = true,
}: UseFeedbackOptions): UseFeedbackReturn {
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackEntry>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasInitialized = useRef(false);
  const feedbackMapRef = useRef(feedbackMap);
  feedbackMapRef.current = feedbackMap;

  // Load from localStorage on mount, then fetch from API
  useEffect(() => {
    if (!enabled || !botId || !sessionId || hasInitialized.current) return;
    hasInitialized.current = true;

    // Load from localStorage immediately for instant display
    const cached = loadFromStorage(botId, sessionId);
    if (Object.keys(cached).length > 0) {
      setFeedbackMap(cached);
    }

    // Then fetch from API to get authoritative state
    const fetchFeedback = async () => {
      try {
        const response = await fetch(
          `/api/bots/${botId}/feedback?sessionId=${sessionId}`
        );
        if (!response.ok) return;

        const json = await response.json();
        const feedback = json.data?.feedback;
        if (!Array.isArray(feedback)) return;

        const map: Record<string, FeedbackEntry> = {};
        for (const item of feedback) {
          map[item.messageId] = {
            rating: item.rating as FeedbackRating,
            ...(item.comment ? { comment: item.comment } : {}),
          };
        }

        setFeedbackMap(map);
        saveToStorage(botId, sessionId, map);
      } catch {
        // Silently fail — localStorage cache is still active
      }
    };

    void fetchFeedback();
  }, [botId, sessionId, enabled]);

  const submitFeedback = useCallback(
    (messageId: string, rating: FeedbackRating, comment?: string) => {
      if (!botId || !sessionId) return;

      // Optimistic update — use ref to read latest feedbackMap without dependency
      const currentMap = feedbackMapRef.current;
      const previousMap = { ...currentMap };
      const newEntry: FeedbackEntry = {
        rating,
        ...(comment ? { comment } : {}),
      };
      const updatedMap = { ...currentMap, [messageId]: newEntry };
      setFeedbackMap(updatedMap);
      saveToStorage(botId, sessionId, updatedMap);
      setError(null);
      setIsSubmitting(true);

      // Submit to API
      const doSubmit = async () => {
        try {
          const response = await fetch(`/api/bots/${botId}/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              messageId,
              sessionId,
              rating,
              ...(comment ? { comment } : {}),
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to submit feedback');
          }
        } catch (err) {
          // Revert optimistic update
          setFeedbackMap(previousMap);
          saveToStorage(botId, sessionId, previousMap);
          setError(err instanceof Error ? err : new Error('Failed to submit feedback'));
        } finally {
          setIsSubmitting(false);
        }
      };

      void doSubmit();
    },
    [botId, sessionId]
  );

  return { feedbackMap, submitFeedback, isSubmitting, error };
}
